import React, { FC, useMemo } from "react";
import { WrapperSection } from "@ldp-web/component-library/ui/atoms/section";
import { GalleryCardScroller } from "@ldp-web/component-library/ui/organisms/galleries/gallery-card-scroller";
import type { ScrollerContentType, GalleryCardScrollerProps } from "@ldp-web/component-library/ui/organisms/galleries/gallery-card-scroller/gallery-card-scroller.types";
import type { FeatureCardsList } from "@ldp-web/component-library/ui/organisms/feature-cards/feature-cards.types";
import { useSDMRouterContext } from "@ldp/sdm-contexts";
import type { IHeaderGroupComponentProps } from "@ldp/common-types";
import { useAnalyticsContext, useFeatureFlags } from '@ldp/common-contexts';
import {
  SDMAnalyticEvents,
  TSDMComponentAnalyticsObject,
  TrackingFnContext,
  getCommonComponentContext,
} from '@ldp/sdm-analytics-utils';
import { useRedirectLink } from '../../utils';
import { useSimpleTranslation } from "@ldp/simple-i18n";
import { HeaderGroupComponent } from "../header-group/header-group.component";

interface IGalleryCardScrollerComponentProps {
  contentId: string;
  componentId: string;
  scrollerContent: ScrollerContentType[];
  scrollOverflow: GalleryCardScrollerProps['scrollOverflow'];
  headerGroup?: IHeaderGroupComponentProps;
  analytics?: TSDMComponentAnalyticsObject;
  position: number;
}

interface IGalleryCardScrollerAnalyticsContext {
  component: {
    id: string;
    name: string;
    position: number;
    image: string;
    type: string;
    cta_url: string;
    subtype: string;
  };
}

interface IGroupContentItem {
  image?: { url?: string } | string;
  callToAction?: {
    url?: string;
    text?: string;
  };
  styling?: unknown;
}

export const parseContent = (
  scrollerContent: ScrollerContentType[],
  redirectToPage: (redirectUrl?: string) => void,
): ScrollerContentType[] => {
  let contentPosition = 0; // used to for analytics tracking

  return scrollerContent
    .filter((item) => item.groupContent.length > 0)
    .map((item) => ({
      ...item,
      contentType:
        item.layout === "1X1" &&
        item.groupContent.length === 1 &&
        // @ts-expect-error - BFF type mismatch
        item.groupContent[0].contentType === "verticalFeatureCard"
          ? "verticalFeatureCard"
          : "horizontalFeatureCard",
      groupContent: item.groupContent.map(
        (content: IGroupContentItem | FeatureCardsList) => {
          contentPosition += 1;

          if ("headline" in content && "displayText1" in content) {
            // Do not format feature cards
            return {
              ...content,
              position: contentPosition,
            };
          }

          let imageUrl = "";
          const image = content.image;
          if (typeof image === "object" && "url" in image) {
            imageUrl = image.url ?? "";
          } else if (typeof image === "string") {
            imageUrl = image;
          }

          const callToActionUrl = content.callToAction?.url;
          const callToActionText = content.callToAction?.text ?? "";

          return {
            ...content,
            // Adding extra props for menu-tile-grid component from WCL
            position: contentPosition,
            image: imageUrl,
            callToActionHandler: () => {
              redirectToPage(callToActionUrl);
            },
            label: callToActionText,
            callToAction: undefined,
            styling: content.styling ?? null,
            linkUrl: callToActionUrl,
          };
        },
      ),
    })) as ScrollerContentType[];
};

export const GalleryCardScrollerComponent: FC<
  IGalleryCardScrollerComponentProps
> = ({
  scrollerContent,
  scrollOverflow,
  headerGroup,
  analytics,
  contentId,
  componentId,
  position,
}) => {
  const i18n = useSimpleTranslation();
  const { router } = useSDMRouterContext();
  const redirectToPage = useRedirectLink(router);
  const { headline, callToAction } = headerGroup || {};
  const { trackEvent } = useAnalyticsContext();
  const { getFeatureFlags } = useFeatureFlags();
  const { EnableHeliosSnowplow: snowplowEnabled } = getFeatureFlags();
  const dsModelData = analytics?.dsModel;
  const commonContext = getCommonComponentContext(
    snowplowEnabled,
    true,
    dsModelData,
    analytics?.genericAnalytics,
  );

  const headerGroupProps: IHeaderGroupComponentProps = headerGroup ?? {
    headline: {
      text: headline?.text || "",
      variantId: "MEDIUM_03",
      callToAction,
      clickHandler: redirectToPage,
    },
  };

  const parsedScrollerContent = useMemo(
    () => parseContent(scrollerContent, redirectToPage),
    [scrollerContent, redirectToPage],
  );
  const lastScrollerElement = parsedScrollerContent[parsedScrollerContent.length - 1];
  const lastScrollerElementPosition =
    lastScrollerElement.groupContent[
      lastScrollerElement.groupContent.length - 1
    ].position;

  const parentComponent = {
    id: contentId,
    type: componentId,
    name: headerGroupProps.headline.text || contentId,
    number_of_children: lastScrollerElementPosition,
    subtype: null,
    position: position + 1,
    is_sponsored: Boolean(analytics?.mediaAnalytics),
  };

  const trackingFunction: TrackingFnContext = (type, context) => {
    const { component } = context as IGalleryCardScrollerAnalyticsContext;

    const eventTrackingContext = {
      event: type,
      component: [
        parentComponent,
        {
          ...component,
          number_of_children: null,
          hierarchy: [parentComponent.name, component.name],
          parent_id: contentId,
          is_sponsored: Boolean(analytics?.mediaAnalytics),
        },
      ],
      ...((type as SDMAnalyticEvents) === SDMAnalyticEvents.UI_ENGAGEMENT
        ? { customEngagement: { action: "click" } }
        : {}),
      ...commonContext,
    };
    trackEvent("heliosGalleryCardScroller", eventTrackingContext);
  };

  const shouldRenderHeaderGroup = Boolean(headerGroupProps.headline.text);

  return (
    <WrapperSection>
      {shouldRenderHeaderGroup ? (
        <HeaderGroupComponent {...headerGroupProps} noWrapper />
      ) : null}
      <GalleryCardScroller
        callToActionHandler={redirectToPage}
        i18nskip={i18n("skip")}
        scrollOverflow={scrollOverflow}
        scrollerContent={parseContent(scrollerContent, redirectToPage)}
        skipLinkLabel={headerGroupProps.headline.text || i18n("a11ySection")}
        trackingFn={trackingFunction}
      />
    </WrapperSection>
  );
};
